import { useTranslation } from "react-i18next";
import type { Decision } from "../../../api/access/decisions";
import type { DatasetVersionSummary } from "../../../api/knowledgeData/knowledgeDataDecoders";
import { publishToResearchSpace } from "../../../api/knowledgeData/releasesApi";
import type { ReleaseSummary, ResearchSpaceProject } from "../../../api/knowledgeData/releaseDecoders";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import ResearchSpaceState from "./ResearchSpaceState";

/**
 * A version's state in ResearchSpace (K-13), and "Publish to ResearchSpace" for a checked
 * version that is not live there: when the project publishes and the installation has one. It
 * takes the decision on releasing, which publishing needs too (`releaseDecision`, GS-8).
 */
export default function PublishToResearchSpace({
  datasetId,
  version,
  researchSpace,
  release,
  publishing,
  onPublished,
}: {
  datasetId: string;
  version: DatasetVersionSummary;
  researchSpace: ResearchSpaceProject;
  release: ReleaseSummary | null;
  publishing: Decision;
  onPublished: () => void;
}) {
  const { t } = useTranslation();
  const submission = useSubmission();
  if (!researchSpace.available) return null;
  const live = release?.liveInResearchSpace ?? false;
  const publication = release?.researchSpace ?? null;
  const offered = researchSpace.publishes && version.checksStatus === "pass" && !live;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="app-text-muted text-xs">{t("data.researchSpace.label")}</span>
      <ResearchSpaceState publication={publication} live={live} detailed />
      {publication === null ? <span className="app-text-muted text-xs">{t("data.researchSpace.notPublished")}</span> : null}
      {offered ? (
        <AppButton
          size="sm"
          requires={publishing}
          disabled={submission.state.status === "submitting"}
          onClick={() => {
            submission.submit(async () => {
              await publishToResearchSpace(datasetId, version.number);
              onPublished();
            });
          }}
        >
          {submission.state.status === "submitting" ? t("data.researchSpace.publishing") : t("data.researchSpace.publish")}
        </AppButton>
      ) : null}
      {submission.state.status === "failed" ? <ErrorNotice error={submission.state.error} compact={true} /> : null}
    </div>
  );
}
